import { useEffect, useRef, useState } from 'react';
import { api, type AnalysisDetail, type AnalysisLineRow, type AnalysisUploadRow } from '../api';
import { money, unit } from '../lib';

// Free Invoice Analysis (ops surface) — upload an invoice, read back parsed spend, catalog matches,
// and a benchmark savings estimate. Internal-only until the privacy terms clear. Upload = the shared
// multipart passthrough; CSV/TXT parse instantly, PDF is stored for a later OCR pass.
const fmtD = (iso: string | null) => {
  if (!iso) return '—';
  const d = iso.slice(0, 10).split('-');
  return d.length === 3 ? `${d[1]}/${d[2]}/${d[0]}` : iso;
};

function LineRow({ l }: { l: AnalysisLineRow }) {
  return (
    <tr className={l.flag ? 'ia-flagged' : ''}>
      <td>
        <div style={{ fontWeight: 600 }}>{l.item_desc ?? '—'}</div>
        <div className="mono muted">{[l.pack_text, l.qty != null ? `qty ${l.qty}` : null, l.vendor_name].filter(Boolean).join(' · ')}</div>
      </td>
      <td>
        {l.matched_item_name
          ? <>{l.matched_item_name}<div className="mono muted">{Math.round((l.match_confidence ?? 0) * 100)}% match</div></>
          : <span className="muted">no catalog match</span>}
      </td>
      <td className="num">{unit(l.uploaded_unit_norm)}</td>
      <td className="num">{unit(l.bench_unit_price)}</td>
      <td className="num">{l.savings_est != null ? money(l.savings_est) : '—'}</td>
      <td>{l.flag ? <span className="pill ia-flag">check pack size</span> : ''}</td>
    </tr>
  );
}

function Detail({ d, onBack }: { d: AnalysisDetail; onBack: () => void }) {
  const s = d.summary;
  return (
    <div className="stack">
      <div className="ia-detail-head">
        <button className="btn-sm ghost" onClick={onBack}>← All uploads</button>
        <span className="mono muted">{d.upload.filename} · parsed via {d.upload.parse_source ?? '—'}</span>
      </div>
      {d.parseNote && <p className="note">{d.parseNote}</p>}
      <section className="cards">
        <div className="stat"><div className="stat-v">{money(s.totalSpend)}</div><div className="stat-sub">Total spend read</div></div>
        <div className="stat"><div className="stat-v">{s.matchedPct}%</div><div className="stat-sub">Lines matched ({s.matched}/{s.lines})</div></div>
        <div className="stat"><div className="stat-v">{money(s.savingsAnnual)}</div><div className="stat-sub">Est. savings/yr — {money(s.savings)} this invoice</div></div>
        <div className="stat"><div className="stat-v">{s.flagged}</div><div className="stat-sub">Flagged lines (check pack size)</div></div>
      </section>
      <p className="sub">Savings vs the historical benchmark (cheapest sane offer per item, same unit of measure). Annualized {s.annualizationNote}. Lines &gt;3× benchmark are flagged, not counted.</p>
      <section className="card">
        <h2>Spend by vendor</h2>
        <table>
          <thead><tr><th>Vendor</th><th className="num">Lines</th><th className="num">Spend</th></tr></thead>
          <tbody>
            {s.byVendor.map((v) => (
              <tr key={v.vendor}><td style={{ fontWeight: 600 }}>{v.vendor}</td><td className="num">{v.lines}</td><td className="num">{money(v.spend)}</td></tr>
            ))}
            {s.byVendor.length === 0 && <tr><td colSpan={3} className="loading">No vendor data on this invoice.</td></tr>}
          </tbody>
        </table>
      </section>
      <section className="card">
        <h2>Line detail</h2>
        <table>
          <thead><tr><th>Invoice line</th><th>Matched item</th><th className="num">Your unit price</th><th className="num">Benchmark</th><th className="num">Savings</th><th></th></tr></thead>
          <tbody>
            {d.lines.map((l) => <LineRow key={l.id} l={l} />)}
            {d.lines.length === 0 && <tr><td colSpan={6} className="loading">No lines parsed.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}

export function InvoiceAnalysisPanel() {
  const [list, setList] = useState<AnalysisUploadRow[] | null>(null);
  const [detail, setDetail] = useState<AnalysisDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadList = () => api.commerceAnalysisList().then(setList).catch((e) => setErr((e as Error).message));
  useEffect(() => { loadList(); }, []);

  async function handleFile(f: File | null | undefined) {
    if (!f || busy) return;
    setBusy(true); setErr(null); setNote(null);
    try {
      const res = await api.commerceAnalysisUpload(f);
      if ('summary' in res) { setDetail(res); if (res.parseNote) setNote(res.parseNote); }
      else setNote(res.note ?? 'Stored.');
      loadList();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function openDetail(id: number) {
    try { setDetail(await api.commerceAnalysisDetail(id)); setErr(null); setNote(null); } catch { /* row may be mid-delete */ }
  }
  async function remove(id: number) {
    try {
      await api.commerceAnalysisDelete(id);
      if (detail && detail.upload.id === id) setDetail(null);
      loadList();
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <div className="stack">
      <h1>Invoice Analysis</h1>
      <p className="sub">Upload a vendor invoice → parsed spend read + savings estimate vs the historical benchmark</p>
      <div
        className={'ia-drop' + (drag ? ' drag' : '')}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); }}
      >
        <div className="ia-drop-title">{busy ? 'Analyzing…' : 'Drop an invoice here'}</div>
        <div className="muted">CSV or TXT parse instantly · PDF is stored (PDF OCR: next iteration) · 10MB max</div>
        <button className="btn-sm" disabled={busy} onClick={() => fileRef.current?.click()}>Choose file</button>
        <input ref={fileRef} type="file" accept=".csv,.txt,.pdf" style={{ display: 'none' }} onChange={(e) => handleFile(e.target.files?.[0])} />
        <div className="ia-privacy muted">Analysis is internal-only until terms sign-off — uploads stay in Tavezio ops and can be deleted below.</div>
      </div>
      {err && <p className="msg bad">⚠ {err}</p>}
      {note && <p className="msg good">{note}</p>}
      {detail ? (
        <Detail d={detail} onBack={() => setDetail(null)} />
      ) : (
        <section className="card">
          <table>
            <thead><tr><th>File</th><th>Uploaded</th><th>Status</th><th className="num">Lines</th><th className="num">Matched</th><th className="num">Spend</th><th className="num">Savings est.</th><th className="num">Flagged</th><th></th></tr></thead>
            <tbody>
              {(list ?? []).map((u) => (
                <tr key={u.id}>
                  <td><button className="link" onClick={() => openDetail(u.id)}>{u.filename}</button></td>
                  <td className="muted">{fmtD(u.created_at)}</td>
                  <td><span className={`pill${u.status === 'parsed' ? ' role-ops' : u.status === 'failed' ? '' : ''}`}>{u.status}{u.parse_source ? ` (${u.parse_source})` : ''}</span></td>
                  <td className="num">{u.lines}</td>
                  <td className="num">{u.lines ? `${u.matched}/${u.lines}` : '—'}</td>
                  <td className="num">{money(u.total_spend)}</td>
                  <td className="num">{money(u.savings_est)}</td>
                  <td className="num">{u.flagged || ''}</td>
                  <td className="num"><button className="btn-sm ghost" onClick={() => remove(u.id)}>Delete</button></td>
                </tr>
              ))}
              {list && list.length === 0 && <tr><td colSpan={9} className="loading">No uploads yet — drop an invoice above to see the analysis.</td></tr>}
            </tbody>
          </table>
          {!list && !err && <p className="loading">Loading…</p>}
        </section>
      )}
    </div>
  );
}
